import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Copy, Check, Download, Upload, RefreshCw, Smartphone, Key, Trash, Info, Lock, QrCode, X } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import BottomNavigation from '../components/BottomNavigation';
import TabNavigation from '../components/TabNavigation';
import { getFromStorage, saveToStorage, get2FABackupCodes, save2FABackupCodes, getVerifiedDevices, removeVerifiedDevice } from '../utils/storage';
import { STORAGE_KEYS, SECURITY_LEVELS, TWO_FA_SETTINGS } from '../utils/constants';
import { t } from '../i18n';

/**
 * İki Faktörlü Kimlik Doğrulama Ekranı
 * Merkeziyetsiz 2FA kurulumunu ve yönetimini sağlar
 */
const TwoFactorAuthScreen = () => {
  const { navigateToScreen, showSecurityVerification } = useUI();
  const { 
    user, 
    is2FAEnabled, 
    setup2FA, 
    verify2FACode, 
    disable2FA, 
    getCurrent2FACode, 
    export2FAData, 
    import2FAData, 
    generate2FAProof 
  } = useAuth();
  
  // Temel durum değişkenleri
  const [activeTab, setActiveTab] = useState('setup');
  const [setupStep, setSetupStep] = useState(0);
  const [setupData, setSetupData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [codeRefreshInterval, setCodeRefreshInterval] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [exportPassword, setExportPassword] = useState('');
  const [exportedData, setExportedData] = useState('');
  const [importData, setImportData] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedItem, setCopiedItem] = useState('');
  const [showRecoveryInfo, setShowRecoveryInfo] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [verifiedDevices, setVerifiedDevices] = useState([]);
  
  // Sekme öğeleri
  const tabItems = [
    { id: 'setup', label: t('security.twoFA.tabs.setup') },
    { id: 'codes', label: t('security.twoFA.tabs.codes') },
    { id: 'backup', label: t('security.twoFA.tabs.backup') },
    { id: 'devices', label: t('security.twoFA.tabs.devices') }
  ];

  // Ekran yüklendiğinde
  useEffect(() => {
    // Doğrulanmış cihazları yükle
    setVerifiedDevices(getVerifiedDevices());
    
    // Yedek kodları yükle
    setBackupCodes(get2FABackupCodes());
    
    // 2FA etkinse kod güncelleyiciyi başlat
    if (is2FAEnabled) {
      updateCurrentCode();
      const interval = setInterval(updateCurrentCode, 1000);
      setCodeRefreshInterval(interval);
    }
    
    return () => {
      if (codeRefreshInterval) {
        clearInterval(codeRefreshInterval);
      }
    };
  }, [is2FAEnabled]);

  // Mevcut 2FA kodunu güncelle
  const updateCurrentCode = () => {
    if (is2FAEnabled) {
      const code = getCurrent2FACode();
      if (code) {
        setCurrentCode(code);
        
        // Zaman kalan süreyi hesapla (30 saniyelik pencere)
        const now = new Date();
        const seconds = now.getSeconds();
        const remaining = 30 - (seconds % 30);
        setTimeRemaining(remaining);
      }
    }
  };

  // 2FA kurulumunu başlat
  const handleStartSetup = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Kullanıcı cüzdan bağlantısını kontrol et
      if (!user?.walletAddress) {
        setError(t('security.twoFA.errors.walletRequired'));
        setIsLoading(false);
        return;
      }
      
      // 2FA kurulumunu başlat
      const data = await setup2FA();
      setSetupData(data);
      setSetupStep(1);
      
      // Yedek kodları oluştur
      generateBackupCodes();
    } catch (err) {
      setError(err.message || t('security.twoFA.errors.setupFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Doğrulama kodunu doğrula
  const verifyCode = () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('security.twoFA.errors.invalidCode'));
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      const verified = verify2FACode(verificationCode);
      
      if (verified) {
        setSuccess(t('security.twoFA.success.verified'));
        setSetupStep(2);
        
        // Kod güncelleyiciyi başlat
        updateCurrentCode();
        const interval = setInterval(updateCurrentCode, 1000);
        setCodeRefreshInterval(interval);
      } else {
        setError(t('security.twoFA.errors.verificationFailed'));
      }
    } catch (err) {
      setError(err.message || t('security.twoFA.errors.generalError'));
    } finally {
      setIsLoading(false);
    }
  };

  // 2FA'yı devre dışı bırak
  const handleDisable2FA = async () => {
    if (!verificationCode) {
      setError(t('security.twoFA.errors.codeRequired'));
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      // Güvenlik doğrulaması
      await showSecurityVerification(() => {
        const disabled = disable2FA(verificationCode);
        
        if (disabled) {
          setSuccess(t('security.twoFA.success.disabled'));
          setVerificationCode('');
          setCurrentCode('');
          setSetupStep(0);
          
          // Yedek kodları temizle
          setBackupCodes([]);
          save2FABackupCodes([]);
          
          // Kod güncelleyiciyi durdur
          if (codeRefreshInterval) {
            clearInterval(codeRefreshInterval);
            setCodeRefreshInterval(null);
          }
        } else {
          setError(t('security.twoFA.errors.disableFailed'));
        }
        
        setIsLoading(false);
      });
    } catch (err) {
      setError(err.message || t('security.twoFA.errors.generalError'));
      setIsLoading(false);
    }
  };

  // Yedek kodları oluştur
  const generateBackupCodes = () => {
    // 10 adet 8 haneli alfanümerik yedek kod oluştur
    const codes = Array(TWO_FA_SETTINGS.BACKUP_CODES_COUNT || 10).fill(0).map(() => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    });
    
    setBackupCodes(codes);
    save2FABackupCodes(codes);
  };

  // Veriyi panoya kopyala
  const copyToClipboard = (text, itemName = '') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(''), 2000);
    });
  };

  // 2FA verilerini dışa aktar
  const handleExport = () => {
    if (!exportPassword || exportPassword.length < 8) {
      setError(t('security.twoFA.errors.passwordTooShort'));
      return;
    }
    
    try {
      const encryptedData = export2FAData(exportPassword);
      
      if (encryptedData) {
        setExportedData(encryptedData);
        setSuccess(t('security.twoFA.success.exported'));
      } else {
        setError(t('security.twoFA.errors.exportFailed'));
      }
    } catch (err) {
      setError(err.message || t('security.twoFA.errors.generalError'));
    }
  };

  // 2FA verilerini içe aktar
  const handleImport = () => {
    if (!importData) {
      setError(t('security.twoFA.errors.noImportData'));
      return;
    }
    
    if (!importPassword) {
      setError(t('security.twoFA.errors.passwordRequired'));
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      const success = import2FAData(importData, importPassword);
      
      if (success) {
        setSuccess(t('security.twoFA.success.imported'));
        setImportData('');
        setImportPassword('');
        
        // Kod güncelleyiciyi başlat
        updateCurrentCode();
        const interval = setInterval(updateCurrentCode, 1000);
        setCodeRefreshInterval(interval);
        
        // Sekmeyi değiştir
        setActiveTab('codes');
      } else {
        setError(t('security.twoFA.errors.importFailed'));
      }
    } catch (err) {
      setError(err.message || t('security.twoFA.errors.generalError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Cihaz silme işlemi
  const handleRemoveDevice = (deviceId) => {
    const removed = removeVerifiedDevice(deviceId);
    
    if (removed) {
      setVerifiedDevices(getVerifiedDevices());
      setSuccess(t('security.twoFA.success.deviceRemoved'));
    } else {
      setError(t('security.twoFA.errors.deviceRemoveFailed'));
    }
    
    setShowRemoveConfirm(false);
  };

  // Recovery Info Modal
  const RecoveryInfoModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">{t('security.twoFA.recoveryInfo.title')}</h3>
          <button 
            onClick={() => setShowRecoveryInfo(false)}
            className="p-2 bg-gray-200 rounded-full"
          >
            <X size={16} />
          </button>
        </div>
        
        <div className="space-y-3 mb-4">
          <p>
            {t('security.twoFA.recoveryInfo.description')}
          </p>
          
          <div className="bg-yellow-50 p-3 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-1">{t('security.twoFA.recoveryInfo.backupCodes.title')}</h4>
            <p className="text-sm text-yellow-700">
              {t('security.twoFA.recoveryInfo.backupCodes.description')}
            </p>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-1">{t('security.twoFA.recoveryInfo.encryptedBackup.title')}</h4>
            <p className="text-sm text-blue-700">
              {t('security.twoFA.recoveryInfo.encryptedBackup.description')}
            </p>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg">
            <h4 className="font-medium text-green-800 mb-1">{t('security.twoFA.recoveryInfo.verifiedDevices.title')}</h4>
            <p className="text-sm text-green-700">
              {t('security.twoFA.recoveryInfo.verifiedDevices.description')}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setShowRecoveryInfo(false)}
          className="w-full py-2 bg-indigo-600 text-white rounded-lg"
        >
          {t('common.understand')}
        </button>
      </div>
    </div>
  );

  // Confirm Remove Modal
  const ConfirmRemoveModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4">
        <div className="text-center mb-4">
          <h3 className="font-bold text-lg">{t('security.twoFA.removeDevice.title')}</h3>
          <p className="text-gray-600">{t('security.twoFA.removeDevice.confirmation')}</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => setShowRemoveConfirm(false)}
            className="flex-1 py-2 border border-gray-300 rounded-lg"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => handleRemoveDevice(showRemoveConfirm)}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout>
      {/* Header */}
      <header className="bg-white p-4 shadow z-10 sticky top-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigateToScreen('profile')}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-bold text-lg ml-2">{t('security.twoFactorAuthTitle')}</h1>
          </div>
          
          <button
            onClick={() => setShowRecoveryInfo(true)}
            className="p-2 bg-indigo-100 text-indigo-700 rounded-full"
          >
            <Info size={20} />
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <TabNavigation 
        items={tabItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-navigation p-4">
        {/* Hata ve Başarı mesajları */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
            <p>{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded">
            <p>{success}</p>
          </div>
        )}
        
        {activeTab === 'setup' && (
          <div className="space-y-4">
            {!is2FAEnabled ? (
              <>
                {setupStep === 0 && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex items-center mb-4">
                      <Shield size={24} className="text-indigo-600 mr-3" />
                      <h2 className="text-xl font-bold">{t('security.twoFA.decentralized')}</h2>
                    </div>
                    
                    <p className="text-gray-600 mb-4">
                      {t('security.twoFA.description')}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <div className="bg-indigo-100 p-2 rounded-full mr-3 mt-1">
                          <Lock size={16} className="text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{t('security.twoFA.features.highSecurity.title')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('security.twoFA.features.highSecurity.description')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                          <QrCode size={16} className="text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{t('security.twoFA.features.easyToUse.title')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('security.twoFA.features.easyToUse.description')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                          <Shield size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{t('security.twoFA.features.decentralized.title')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('security.twoFA.features.decentralized.description')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center"
                      onClick={handleStartSetup}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <RefreshCw size={18} className="mr-2 animate-spin" />
                          {t('security.twoFA.buttons.startingSetup')}
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Shield size={18} className="mr-2" />
                          {t('security.twoFA.buttons.startSetup')}
                        </span>
                      )}
                    </button>
                  </div>
                )}

                {setupStep === 1 && setupData && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h2 className="text-xl font-bold mb-4">{t('security.twoFA.scanQRCode')}</h2>
                    
                    <p className="text-gray-600 mb-4">
                      {t('security.twoFA.scanQRDescription')}
                    </p>
                    
                    <div className="flex justify-center mb-6">
                      <div className="bg-white p-2 border border-gray-300 rounded-lg">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(setupData.otpAuthUrl)}&size=200x200`} 
                          alt="2FA QR Code"
                          className="w-52 h-52"
                        />
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-6">
                      {t('security.twoFA.manualKeyInstructions')}
                    </p>
                    
                    <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg mb-6">
                      <code className="font-mono text-sm break-all">
                        {setupData.secretKeyBase32}
                      </code>
                      <button
                        className="ml-2 p-2 text-gray-500 hover:text-indigo-600"
                        onClick={() => copyToClipboard(setupData.secretKeyBase32, 'key')}
                      >
                        {copiedItem === 'key' ? (
                          <Check size={18} className="text-green-500" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">{t('security.twoFA.verificationCode')}</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder={t('security.twoFA.enterSixDigitCode')}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        maxLength={6}
                      />
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        className="flex-1 py-3 border border-gray-300 rounded-lg font-medium"
                        onClick={() => setSetupStep(0)}
                        disabled={isLoading}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-medium"
                        onClick={verifyCode}
                        disabled={isLoading || verificationCode.length !== 6}
                      >
                        {isLoading ? t('security.twoFA.buttons.verifying') : t('security.twoFA.buttons.verify')}
                      </button>
                    </div>
                  </div>
                )}

                {setupStep === 2 && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check size={40} className="text-green-600" />
                      </div>
                      <h2 className="text-xl font-bold mb-2">{t('security.twoFA.success.enabled')}</h2>
                      <p className="text-gray-600">
                        {t('security.twoFA.success.enabledDescription')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                      <h3 className="font-medium text-yellow-800 mb-2">{t('security.twoFA.saveBackupCodes')}</h3>
                      <p className="text-sm text-yellow-700 mb-3">
                        {t('security.twoFA.backupCodesDescription')}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, index) => (
                          <div key={index} className="bg-white p-2 rounded border border-yellow-200 font-mono text-center text-sm">
                            {code}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <button
                      className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium"
                      onClick={() => {
                        setActiveTab('codes');
                        setVerificationCode('');
                      }}
                    >
                      {t('common.ok')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <Check size={20} className="text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold">{t('security.twoFA.status.enabled')}</h2>
                </div>
                
                <p className="text-gray-600 mb-6">
                  {t('security.twoFA.disableInstructions')}
                </p>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">{t('security.twoFA.verificationCode')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('security.twoFA.enterSixDigitCode')}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                    maxLength={6}
                  />
                </div>
                
                <button
                  className="w-full py-3 bg-red-600 text-white rounded-lg font-medium flex items-center justify-center"
                  onClick={handleDisable2FA}
                  disabled={isLoading || verificationCode.length !== 6}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <RefreshCw size={18} className="mr-2 animate-spin" />
                      {t('common.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Shield size={18} className="mr-2" />
                      {t('security.twoFA.buttons.disable')}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'codes' && (
          <div className="space-y-4">
            {is2FAEnabled ? (
              <>
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h2 className="text-xl font-bold mb-4">{t('security.twoFA.currentCode')}</h2>
                  
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl font-mono font-bold mb-2 tracking-widest">
                      {currentCode ? currentCode.split('').join(' ') : '- - - - - -'}
                    </div>
                    
                    <div className="w-full bg-gray-200 h-2 rounded-full mb-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-200" 
                        style={{ width: `${(timeRemaining / 30) * 100}%` }}
                      ></div>
                    </div>
                    
                    <p className="text-gray-500 text-sm">
                      {timeRemaining > 0 ? 
                        t('security.twoFA.codeRefreshTime', { seconds: timeRemaining }) : 
                        t('security.twoFA.codeRefreshing')}
                    </p>
                  </div>
                  
                  <button
                    className="w-full py-3 border border-indigo-600 text-indigo-600 rounded-lg font-medium flex items-center justify-center"
                    onClick={() => copyToClipboard(currentCode, 'currentCode')}
                  >
                    {copiedItem === 'currentCode' ? (
                      <span className="flex items-center">
                        <Check size={18} className="mr-2 text-green-500" />
                        {t('common.copied')}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Copy size={18} className="mr-2" />
                        {t('security.twoFA.buttons.copyCode')}
                      </span>
                    )}
                  </button>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-4 mt-4">
                  <h2 className="text-xl font-bold mb-4">{t('security.twoFA.backupCodes')}</h2>
                  
                  <p className="text-gray-600 mb-4">
                    {t('security.twoFA.backupCodesInstructions')}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="bg-gray-100 p-2 rounded border border-gray-200 font-mono text-center text-sm flex justify-between items-center">
                        <span>{code}</span>
                        <button
                          className="text-gray-500 hover:text-indigo-600"
                          onClick={() => copyToClipboard(code, `code-${index}`)}
                        >
                          {copiedItem === `code-${index}` ? (
                            <Check size={16} className="text-green-500" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      className="flex-1 py-3 border border-indigo-600 text-indigo-600 rounded-lg font-medium flex items-center justify-center"
                      onClick={() => copyToClipboard(backupCodes.join('\n'), 'all-codes')}
                    >
                      {copiedItem === 'all-codes' ? (
                        <span className="flex items-center">
                          <Check size={18} className="mr-2 text-green-500" />
                          {t('common.copied')}
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Copy size={18} className="mr-2" />
                          {t('security.twoFA.buttons.copyAll')}
                        </span>
                      )}
                    </button>
                    <button
                      className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center"
                      onClick={generateBackupCodes}
                    >
                      <RefreshCw size={18} className="mr-2" />
                      {t('security.twoFA.buttons.generateNewCodes')}
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h2 className="text-xl font-bold mb-4">{t('security.twoFA.blockchainVerification')}</h2>
                  
                  <p className="text-gray-600 mb-4">
                    {t('security.twoFA.blockchainVerificationDescription')}
                  </p>
                  
                  <div className="bg-gray-100 p-3 rounded-lg mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('security.twoFA.lastVerification')}</span>
                      <span className="text-xs text-gray-500">{t('security.twoFA.timeAgo', { time: '2' })}</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <div className="bg-green-100 p-1.5 rounded-full mr-2">
                        <Check size={14} className="text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">{t('security.twoFA.verifiedOnPolygon')}</span>
                    </div>
                  </div>
                  
                  <button
                    className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center"
                    onClick={() => {
                      // Blockchain doğrulama işlemi
                      const proof = generate2FAProof();
                      alert(t('security.twoFA.proofGenerated', { proof: proof?.merkleRoot?.substring(0, 10) }));
                    }}
                  >
                    <Key size={18} className="mr-2" />
                    {t('security.twoFA.buttons.generateProof')}
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock size={32} className="text-gray-400" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">{t('security.twoFA.status.disabled')}</h2>
                  <p className="text-gray-600 mb-6">
                    {t('security.twoFA.enableFirstToViewCodes')}
                  </p>
                  <button
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium"
                    onClick={() => setActiveTab('setup')}
                  >
                    {t('security.twoFA.buttons.goToSetup')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'backup' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-xl font-bold mb-4">{t('security.twoFA.backupAndRecovery')}</h2>
              
              <p className="text-gray-600 mb-6">
                {t('security.twoFA.backupDescription')}
              </p>
              
              <div className="border-t border-gray-200 pt-4 mb-6">
                <h3 className="font-bold text-lg mb-4">{t('security.twoFA.exportData')}</h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  {t('security.twoFA.exportDescription')}
                </p>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">{t('security.twoFA.password')}</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('security.twoFA.passwordPlaceholder')}
                    value={exportPassword}
                    onChange={(e) => setExportPassword(e.target.value)}
                  />
                </div>
                
                <button
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center mb-4"
                  onClick={handleExport}
                  disabled={!is2FAEnabled || !exportPassword || exportPassword.length < 8}
                >
                  <Download size={18} className="mr-2" />
                  {t('security.twoFA.buttons.export')}
                </button>
                
                {exportedData && (
                  <div className="mt-4">
                    <label className="block text-gray-700 mb-2">{t('security.twoFA.encryptedData')}</label>
                    <div className="bg-gray-100 p-3 rounded-lg mb-2 relative">
                      <textarea
                        className="w-full h-24 bg-transparent focus:outline-none font-mono text-sm"
                        value={exportedData}
                        readOnly
                      />
                      <button
                        className="absolute top-2 right-2 p-2 text-gray-500 hover:text-indigo-600"
                        onClick={() => copyToClipboard(exportedData, 'exported-data')}
                      >
                        {copiedItem === 'exported-data' ? (
                          <Check size={18} className="text-green-500" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('security.twoFA.storeDataSafely')}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-bold text-lg mb-4">{t('security.twoFA.importData')}</h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  {t('security.twoFA.importDescription')}
                </p>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">{t('security.twoFA.encryptedData')}</label>
                  <textarea
                    className="w-full h-24 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    placeholder={t('security.twoFA.pasteEncryptedData')}
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">{t('security.twoFA.password')}</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('security.twoFA.enterPassword')}
                    value={importPassword}
                    onChange={(e) => setImportPassword(e.target.value)}
                  />
                </div>
                
                <button
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center"
                  onClick={handleImport}
                  disabled={isLoading || !importData || !importPassword}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <RefreshCw size={18} className="mr-2 animate-spin" />
                      {t('common.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Upload size={18} className="mr-2" />
                      {t('security.twoFA.buttons.import')}
                    </span>
                  )}
                </button>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-xl font-bold mb-4">{t('security.twoFA.arweaveBackup')}</h2>
              
              <p className="text-gray-600 mb-4">
                {t('security.twoFA.arweaveDescription')}
              </p>
              
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-1.5 rounded-full mr-2 mt-0.5">
                    <Shield size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-800">{t('security.twoFA.decentralizedStorage')}</h4>
                    <p className="text-sm text-blue-700">
                      {t('security.twoFA.arweaveTokenRequired')}
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center"
                onClick={() => {
                  // Arweave yedekleme işlemi
                  // Bu kısım gerçek bir uygulama için Arweave API entegrasyonu gerektirir
                  alert(t('security.twoFA.arweaveBackupStarted'));
                }}
                disabled={!is2FAEnabled}
              >
                <Upload size={18} className="mr-2" />
                {t('security.twoFA.buttons.backupToArweave')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'devices' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-xl font-bold mb-4">{t('security.twoFA.verifiedDevices')}</h2>
              
              <p className="text-gray-600 mb-4">
                {t('security.twoFA.devicesDescription')}
              </p>
              
              {verifiedDevices.length > 0 ? (
                <div className="space-y-3 mb-4">
                  {verifiedDevices.map((device) => (
                    <div key={device.id} className="bg-gray-100 p-3 rounded-lg flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="bg-indigo-100 p-2 rounded-full mr-3">
                          <Smartphone size={20} className="text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">{device.name}</p>
                          <p className="text-xs text-gray-500">{device.lastUsed}</p>
                        </div>
                      </div>
                      <button
                        className="p-2 text-gray-500 hover:text-red-600"
                        onClick={() => setShowRemoveConfirm(device.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Smartphone size={40} className="mx-auto mb-2 text-gray-400" />
                  <p>{t('security.twoFA.noVerifiedDevices')}</p>
                </div>
              )}
              
              <div className="bg-green-50 p-3 rounded-lg mb-4">
                <div className="flex items-start">
                  <div className="bg-green-100 p-1.5 rounded-full mr-2 mt-0.5">
                    <Lock size={14} className="text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-green-800">{t('security.twoFA.thisDevice')}</h4>
                    <p className="text-sm text-green-700">
                      {t('security.twoFA.deviceVerificationDescription')}
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                className="w-full py-3 border border-indigo-600 text-indigo-600 rounded-lg font-medium flex items-center justify-center"
                onClick={() => {
                  // Şu anki cihazı doğrula
                  const newDevice = {
                    id: `device-${Date.now()}`,
                    name: navigator.userAgent.includes('Mobile') ? t('security.twoFA.deviceTypes.mobile') : t('security.twoFA.deviceTypes.desktop'),
                    lastUsed: new Date().toLocaleDateString()
                  };
                  
                  const updatedDevices = [...verifiedDevices, newDevice];
                  setVerifiedDevices(updatedDevices);
                  saveToStorage(STORAGE_KEYS.VERIFIED_DEVICES, updatedDevices);
                  setSuccess(t('security.twoFA.success.deviceVerified'));
                }}
                disabled={!is2FAEnabled}
              >
                <Smartphone size={18} className="mr-2" />
                {t('security.twoFA.buttons.verifyThisDevice')}
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-xl font-bold mb-4">{t('security.twoFA.deviceConnectionHistory')}</h2>
              
              <div className="space-y-3">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">Chrome - Windows</span>
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">{t('security.active')}</span>
                  </div>
                  <p className="text-xs text-gray-500">{t('security.twoFA.lastAccess', { time: t('security.today') + ', 14:32' })}</p>
                  <p className="text-xs text-gray-500">IP: 192.168.1.1</p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">Safari - iPhone</span>
                    <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded">{t('security.twoFA.daysAgo', { days: 3 })}</span>
                  </div>
                  <p className="text-xs text-gray-500">{t('security.twoFA.lastAccess', { time: '7 Mart 2023, 09:15' })}</p>
                  <p className="text-xs text-gray-500">IP: 185.93.xx.xx</p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">Firefox - Mac</span>
                    <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded">{t('security.twoFA.weeksAgo', { weeks: 1 })}</span>
                  </div>
                  <p className="text-xs text-gray-500">{t('security.twoFA.lastAccess', { time: '1 Mart 2023, 21:48' })}</p>
                  <p className="text-xs text-gray-500">IP: 78.45.xx.xx</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeItem="profile" />
      
      {/* Modals */}
      {showRecoveryInfo && <RecoveryInfoModal />}
      {showRemoveConfirm && <ConfirmRemoveModal />}
    </PageLayout>
  );
};

export default TwoFactorAuthScreen;